import { z } from 'zod';

import {
  BrandExperienceFrequency,
  BrandExperienceTypes,
  BrandMarketingDescription,
  BrandReach,
  Industry,
} from './brand-marketing';
import { ContactSchema } from './contact';
import { AiCurationTier, AVAILABLE_BRAND_FEATURES, THEME } from './settings';
import { BooleanStringSchema, EmailStringSchema, HexColorSchema } from '../../common';
import { buildQueryString } from '../../utils/querystring';
import { AiCurationAudience, AiCurationResource, AiCurationSpace } from '../ai/curation';
import { makeEndpoint, paginateResponseSchema, PhoneNumberValidationSchema, sortOptions } from '../common';
import { AllCountries } from '../config/data/countries';
import { LocationSchema } from '../location/location';
import { LoyaltyProgramSchema } from '../loyalty/loyalty-program';
import { IMediaReferenceSchema } from '../media/media';
import { StripeAccountServiceAgreement, StripeAccountStatus } from '../payment-method/const';
import { TranslationSchema, TranslationWithIdSchema } from '../translation';
import { UserSchema } from '../user/user';
import { isValidCurrencyCode } from '../validator/currencyCode';

export enum BrandReviewStatus {
  PENDING = 'pending',
  UNDER_REVIEW = 'under_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
}

export enum Tier {
  GROWTH = 'growth',
  PRO = 'pro',
  STARTER = 'starter',
  STANDARD = 'standard',
  CUSTOM = 'custom',
}

export enum LanguageCode {
  AR_SA = 'ar-SA',
  BG_BG = 'bg-BG',
  CS_CZ = 'cs-CZ',
  DE_DE = 'de-DE',
  EN_GB = 'en-GB',
  EN_US = 'en-US',
  ES_ES = 'es-ES',
  FI_FI = 'fi-FI',
  FR_FR = 'fr-FR',
  ID_ID = 'id-ID',
  IT_IT = 'it-IT',
  JA_JP = 'ja-JP',
  KO_KR = 'ko-KR',
  NL_NL = 'nl-NL',
  PL_PL = 'pl-PL',
  PT_BR = 'pt-BR',
  RO_RO = 'ro-RO',
  RU_RU = 'ru-RU',
  SV_SE = 'sv-SE',
  TH_TH = 'th-TH',
  TR_TR = 'tr-TR',
  VI_VN = 'vi-VN',
  ZH_HANS = 'zh-Hans',
  ZH_HANT = 'zh-Hant',
}

export type Location = z.infer<typeof LocationSchema>;

export enum BrandCategory {
  LUXURY = 'luxury',
  LIFESTYLE = 'lifestyle',
  BOUTIQUE = 'boutique',
  INDEPENDENT = 'independent',
  DESTINATION = 'destination',
}

export enum SelectLoyaltyReferenceIds {
  ALASKA = 'ALK',
}

export enum TravelProfile {
  LUXURY_TRAVELERS = 'luxury-travelers',
  FAMILY_TRAVELERS = 'family-travelers',
  ADVENTURE_SEEKERS = 'adventure-seekers',
  NATURE_LOVERS = 'nature-lovers',
  CULTURE_EXPLORERS = 'culture-explorers',
  DIGITAL_NOMADS = 'digital-nomads',
  RELAXATION_WELLNESS_SEEKERS = 'relaxation-wellness-seekers',
}

export enum WayVersion {
  V1 = 'V1',
  V2 = 'V2',
  V3 = 'V3',
}

export enum ProductLine {
  ACTIVATE = 'activate',
  HOST = 'host',
  RESERVE = 'reserve',
}

// DisplayedProductLine represents configuration buckets that group features and products
// These are NOT actual product lines and do not apply to listings - they are display/config options only
export enum DisplayedProductLine {
  LOYALTY = 'loyalty',
  PARTNER = 'partner',
  CURATION = 'curation',
}

const BrandSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  tier: z.nativeEnum(Tier, { invalid_type_error: 'Invalid tier supplied' }),
  products: z.array(z.nativeEnum(ProductLine)).nullable(),
  displayedProductLines: z.array(z.nativeEnum(DisplayedProductLine)).nullable().optional(),
  countryCode: z.enum(Object.keys(AllCountries) as [keyof typeof AllCountries, ...(keyof typeof AllCountries)[]]),
  currency: z.string().refine((code) => isValidCurrencyCode(code), {
    message: 'Currency is invalid',
  }),
  defaultLanguageCode: z.nativeEnum(LanguageCode),
  manuallyTranslatedLanguages: z.array(z.nativeEnum(LanguageCode)).nullable(),
  creditCardFeePercentage: z.number().min(0).max(100, 'Credit card fee % must be less than 100'),
  features: z.record(z.string(), z.boolean()),
  locationId: z.string().uuid().nullable(),
  location: LocationSchema.omit({
    additionalInfo: true,
  }).nullable(),
  category: z.nativeEnum(BrandCategory).nullable(),
  adr: z.number().nullable(),
  keys: z.number().nullable(),
  propertyType: z.string().nullable().optional(),
  vision: z.string().nullable(),
  pillars: z.string().nullable(),
  travelProfile: z.nativeEnum(TravelProfile).nullable(),
  availableSpacesAndAmenities: z.string().nullable(),
  audiences: z.array(z.nativeEnum(AiCurationAudience)),
  spaces: z.array(z.nativeEnum(AiCurationSpace)),
  resources: z.array(z.nativeEnum(AiCurationResource)),
  contacts: z.array(ContactSchema).optional(),
  churnedAt: z.string().datetime().nullable().optional(),
  brandLaunchedAt: z.string().datetime().nullable(),
  isTestBrand: z.boolean().nullable().optional(),
  isCuratedBrand: z.boolean().nullable().optional(),
  csmUserId: z.string().uuid().nullable().optional(),
  organizationId: z.string().uuid().nullable().optional(),
  enableAutomaticExperienceSorting: z.boolean().optional(),
  isLimited: z.boolean().optional().default(false),
  status: z.nativeEnum(BrandReviewStatus).optional().default(BrandReviewStatus.APPROVED),
  aiCurationTier: z.nativeEnum(AiCurationTier).optional().default(AiCurationTier.FREE),
  isOnWaitingList: z.boolean().optional().default(false),
  preventOverlappingBookings: z.boolean().optional().default(false),
});

const BrandMarketingSchema = z
  .object({
    industry: z.nativeEnum(Industry),
    description: z.nativeEnum(BrandMarketingDescription).optional().nullable(),
    experienceFrequency: z.nativeEnum(BrandExperienceFrequency).optional().nullable(),
    marketingCategories: z.array(z.string()).optional().nullable(),
    brandOverview: z.string().optional().nullable(),
    experienceVision: z.string().optional().nullable(),
    experienceTypes: z.array(z.nativeEnum(BrandExperienceTypes)).optional().nullable(),
    website: z.string().optional().nullable(),
    reach: z.nativeEnum(BrandReach).optional().nullable(),
  })
  .optional()
  .nullable();

export const CreateBrandRequestSchema = BrandSchema.pick({
  name: true,
  tier: true,
  countryCode: true,
  currency: true,
  defaultLanguageCode: true,
  creditCardFeePercentage: true,
  features: true,
  contacts: true,
  isTestBrand: true,
  isCuratedBrand: true,
  csmUserId: true,
  products: true,
  displayedProductLines: true,
  aiCurationTier: true,
})
  .extend({
    owner: z.object({
      firstName: z
        .string()
        .min(2, 'First name must be at least 2 characters')
        .max(100, 'First name must be less than 100 characters'),
      lastName: z
        .string()
        .min(2, 'Last name must be at least 2 characters')
        .max(100, 'Last name must be less than 100 characters'),
      emailAddress: EmailStringSchema.max(255, 'Email Address must be less than 255 characters'),
      phoneNumber: PhoneNumberValidationSchema.optional(),
    }),
    isBrandLaunched: z.boolean().optional(),
    csmUserId: z.string().uuid().optional(),
    organizationId: z.string().uuid().optional(),
    externalReferenceId: z.string().optional(),
    wayVersion: z.nativeEnum(WayVersion).optional(),
  })
  .strict();

export type CreateBrandRequestType = z.infer<typeof CreateBrandRequestSchema>;

export const UpdateBrandRequestSchema = BrandSchema.omit({
  id: true,
  churnedAt: true,
  location: true,
  isOnWaitingList: true,
})
  .extend({
    emailAddress: EmailStringSchema,
    location: z
      .object({
        name: z.string(),
        longitude: z.number(),
        latitude: z.number(),
      })
      .optional(),
    globalCommissionPercentage: z.number().min(0).max(100),
    creditCardFeePaidByBrand: z.boolean(),
    isBrandLaunched: z.boolean().optional(),
    externalReferenceId: z.string().optional(),
    enableAutomaticExperienceSorting: z.boolean().optional(),
    wayVersion: z.nativeEnum(WayVersion).optional(),
    marketing: BrandMarketingSchema.optional().nullable(),
  })
  .partial()
  .strict();

export type UpdateBrandRequestType = z.infer<typeof UpdateBrandRequestSchema>;

// Host Signup Settings Response Schema

const HostSignupSettingsDesignElementSchema = z.object({
  logoId: z.string().uuid().nullable(),
  logo: IMediaReferenceSchema.nullable().optional(),
  identityImageId: z.string().uuid().nullable(),
  identityImage: IMediaReferenceSchema.nullable().optional(),
  primaryColor: HexColorSchema.nullable(),
  secondaryColor: HexColorSchema.nullable(),
  buttonColor: HexColorSchema.nullable(),
  primaryFont: z.string().nullable(),
  secondaryFont: z.string().nullable(),
  theme: z.nativeEnum(THEME).nullable(),
});

const HostSignupSettingsHostSignupSchema = z.object({
  headingText: z.string().optional(),
  experienceOverview: z.string().optional(),
  joinCommunityOverview: z.string().optional(),
  hostFeaturedImageId: z.string().nullable().optional(),
  hostFeaturedImage: IMediaReferenceSchema.nullable().optional(),
  experienceFeaturedImageId: z.string().nullable().optional(),
  experienceFeaturedImage: IMediaReferenceSchema.nullable().optional(),
  signupFeaturedImageId: z.string().nullable().optional(),
  signupFeaturedImage: IMediaReferenceSchema.nullable().optional(),
});

const GetHostSignupSettingsQuerySchema = z
  .object({
    include: z.string().optional(),
  })
  .default({});

const GetHostSignupSettingsResponseSchema = z.object({
  name: z.string(),
  countryCode: z.string(),
  emailAddress: z.string(),
  designElement: HostSignupSettingsDesignElementSchema,
  hostSignup: HostSignupSettingsHostSignupSchema.optional(),
  loyaltyProgram: LoyaltyProgramSchema.nullable().optional(),
});

export type GetHostSignupSettingsResponse = z.infer<typeof GetHostSignupSettingsResponseSchema>;

export const GetHostSignupSettingsEndpoint = makeEndpoint({
  method: 'GET',
  url: (params: { [key: string]: unknown; query: { include?: string } }) =>
    params.query?.include
      ? `v1/brands/${params['brandId'] as string}/settings/host-signup?${buildQueryString(params.query)}`
      : `v1/brands/${params['brandId'] as string}/settings/host-signup`,
  querySchema: GetHostSignupSettingsQuerySchema,
  requestSchema: z.void(),
  responseSchema: GetHostSignupSettingsResponseSchema,
});

export const BrandDetailResponseSchema = BrandSchema.omit({
  csmUserId: true,
  location: true,
})
  .extend({
    emailAddress: EmailStringSchema,
    creditCardFeePaidByBrand: z.boolean(),
    globalCommissionPercentage: z.number().nullable(),
    paymentPlatform: z.string(),
    stripeAccountStatus: z.nativeEnum(StripeAccountStatus).nullable().optional(),
    features: z.object({
      active: z
        .array(z.string())
        .transform((features) =>
          features.filter((feature): feature is AVAILABLE_BRAND_FEATURES =>
            Object.values(AVAILABLE_BRAND_FEATURES).includes(feature as AVAILABLE_BRAND_FEATURES),
          ),
        )
        .optional(),
      hostsLimit: z.number().optional(),
      updatedAt: z.string().datetime().optional(),
    }),
    location: LocationSchema.omit({
      additionalInfo: true,
      createdAt: true,
      updatedAt: true,
    }).nullable(),
    csmUser: UserSchema.pick({
      id: true,
      firstName: true,
      lastName: true,
    }).nullable(),
    externalReferenceId: z.string().nullable(),
    enableAutomaticExperienceSorting: z.boolean(),
    wayVersion: z.nativeEnum(WayVersion),
    manuallyTranslatedLanguages: z.array(z.nativeEnum(LanguageCode)).nullable(),
    wayCode: z.string().nullable(),
    displayedProductLines: z.array(z.nativeEnum(DisplayedProductLine)).nullable().optional(),
    marketing: BrandMarketingSchema,
  })
  .strict();

export enum BrandQueryFilter {
  CHURNED = 'churned',
  DELETED = 'deleted',
  ACTIVE = 'active',
}

export enum BrandSortBy {
  NAME = 'name',
  EMAIL = 'emailAddress',
  TIER = 'tier',
  FEE = 'creditCardFeePercentage',
}

export const GetBrandsQuerySchema = z
  .object({
    q: z.string().optional(),
    status: z.nativeEnum(BrandQueryFilter).optional(),
    reviewStatus: z.nativeEnum(BrandReviewStatus).optional(),
    isTestBrand: z
      .boolean()
      .or(BooleanStringSchema.transform((str) => str.toLowerCase() === 'true'))
      .optional(),
    isCuratedBrand: z
      .boolean()
      .or(BooleanStringSchema.transform((str) => str.toLowerCase() === 'true'))
      .optional(),
    organizationId: z.string().optional(),
    page: z.coerce.number().min(1).default(1),
    limit: z.coerce.number().min(1).default(10),
    sortBy: z.nativeEnum(BrandSortBy).optional(),
    sortDir: z.enum(sortOptions).optional(),
  })
  .strict();

export const GetOrganizationBrandsQuerySchema = z
  .object({
    page: z.coerce.number().min(1).default(1),
    limit: z.coerce.number().min(1).default(10),
    sortBy: z.nativeEnum(BrandSortBy).optional(),
    sortDir: z.enum(sortOptions).optional(),
  })
  .strict();

export const BrandPaginatedResponseSchema = paginateResponseSchema(
  BrandSchema.pick({
    id: true,
    name: true,
    tier: true,
    countryCode: true,
    currency: true,
    creditCardFeePercentage: true,
    isTestBrand: true,
    isCuratedBrand: true,
    churnedAt: true,
    organizationId: true,
    products: true,
    displayedProductLines: true,
    isLimited: true,
    status: true,
  })
    .extend({
      emailAddress: EmailStringSchema,
      externalReferenceId: z.string().nullable().optional(),
      csmUser: UserSchema.pick({
        id: true,
        firstName: true,
        lastName: true,
      }).nullable(),
      logoUri: z.string().nullable(),
      deletedAt: z.string().datetime().nullable(),
    })
    .strict(),
);

export const BrandPaginatedByUserResponseSchema = paginateResponseSchema(
  BrandSchema.pick({
    id: true,
    name: true,
    tier: true,
    // products: true,
  })
    .extend({
      emailAddress: EmailStringSchema,
      brandLogo: IMediaReferenceSchema.nullable(),
    })
    .strict(),
);

export const PaginatedBrandsEndpoint = makeEndpoint({
  method: 'GET',
  url: () => `brands`,
  responseSchema: BrandPaginatedResponseSchema,
  requestSchema: GetBrandsQuerySchema,
});

// these are the country codes that are supported by Stripe
// recipient agreement accounts will be able to support all of these
export enum StripeSupportedCountryCodes {
  AE = 'AE',
  AG = 'AG',
  AL = 'AL',
  AM = 'AM',
  AO = 'AO',
  AR = 'AR',
  AT = 'AT',
  AU = 'AU',
  AZ = 'AZ',
  BA = 'BA',
  BD = 'BD',
  BE = 'BE',
  BG = 'BG',
  BH = 'BH',
  BJ = 'BJ',
  BN = 'BN',
  BO = 'BO',
  BR = 'BR',
  BS = 'BS',
  BT = 'BT',
  BW = 'BW',
  CA = 'CA',
  CH = 'CH',
  CI = 'CI',
  CL = 'CL',
  CO = 'CO',
  CR = 'CR',
  CY = 'CY',
  CZ = 'CZ',
  DE = 'DE',
  DK = 'DK',
  DO = 'DO',
  DZ = 'DZ',
  EC = 'EC',
  EE = 'EE',
  EG = 'EG',
  ES = 'ES',
  ET = 'ET',
  FI = 'FI',
  FR = 'FR',
  GA = 'GA',
  GB = 'GB',
  GH = 'GH',
  GI = 'GI',
  GM = 'GM',
  GR = 'GR',
  GT = 'GT',
  GY = 'GY',
  HK = 'HK',
  HR = 'HR',
  HU = 'HU',
  ID = 'ID',
  IE = 'IE',
  IL = 'IL',
  IN = 'IN',
  IS = 'IS',
  IT = 'IT',
  JM = 'JM',
  JO = 'JO',
  JP = 'JP',
  KE = 'KE',
  KH = 'KH',
  KR = 'KR',
  KW = 'KW',
  KZ = 'KZ',
  LA = 'LA',
  LC = 'LC',
  LI = 'LI',
  LK = 'LK',
  LT = 'LT',
  LU = 'LU',
  LV = 'LV',
  MA = 'MA',
  MC = 'MC',
  MD = 'MD',
  MG = 'MG',
  MK = 'MK',
  MN = 'MN',
  MO = 'MO',
  MT = 'MT',
  MU = 'MU',
  MX = 'MX',
  MY = 'MY',
  MZ = 'MZ',
  NA = 'NA',
  NE = 'NE',
  NG = 'NG',
  NL = 'NL',
  NO = 'NO',
  NZ = 'NZ',
  OM = 'OM',
  PA = 'PA',
  PE = 'PE',
  PH = 'PH',
  PK = 'PK',
  PL = 'PL',
  PT = 'PT',
  PY = 'PY',
  QA = 'QA',
  RO = 'RO',
  RS = 'RS',
  RW = 'RW',
  SA = 'SA',
  SE = 'SE',
  SG = 'SG',
  SI = 'SI',
  SK = 'SK',
  SM = 'SM',
  SN = 'SN',
  SV = 'SV',
  TH = 'TH',
  TN = 'TN',
  TR = 'TR',
  TT = 'TT',
  TW = 'TW',
  TZ = 'TZ',
  US = 'US',
  UY = 'UY',
  UZ = 'UZ',
  VN = 'VN',
  ZA = 'ZA',
}

export enum SupportedCountryCodeForFullAgreement {
  AE = 'AE',
  AT = 'AT',
  AU = 'AU',
  BE = 'BE',
  CA = 'CA',
  CH = 'CH',
  CY = 'CY',
  CZ = 'CZ',
  DE = 'DE',
  DK = 'DK',
  EE = 'EE',
  ES = 'ES',
  FI = 'FI',
  FR = 'FR',
  GB = 'GB',
  HR = 'HR',
  HU = 'HU',
  IE = 'IE',
  IT = 'IT',
  JP = 'JP',
  KR = 'KR',
  LT = 'LT',
  LU = 'LU',
  LV = 'LV',
  MT = 'MT',
  MX = 'MX',
  NL = 'NL',
  PL = 'PL',
  PT = 'PT',
  RO = 'RO',
  SE = 'SE',
  SI = 'SI',
  SK = 'SK',
  US = 'US',
}

export type SupportedCountryCode = StripeSupportedCountryCodes | SupportedCountryCodeForFullAgreement;

export enum Currency {
  AED = 'AED',
  ALL = 'ALL',
  AMD = 'AMD',
  AOA = 'AOA',
  ARS = 'ARS',
  AUD = 'AUD',
  AZN = 'AZN',
  BAM = 'BAM',
  BDT = 'BDT',
  BGN = 'BGN',
  BHD = 'BHD',
  BND = 'BND',
  BOB = 'BOB',
  BRL = 'BRL',
  BSD = 'BSD',
  BTN = 'BTN',
  BWP = 'BWP',
  CAD = 'CAD',
  CHF = 'CHF',
  CLP = 'CLP',
  CNY = 'CNY',
  COP = 'COP',
  CRC = 'CRC',
  CZK = 'CZK',
  DKK = 'DKK',
  DOP = 'DOP',
  DZD = 'DZD',
  EGP = 'EGP',
  ETB = 'ETB',
  EUR = 'EUR',
  GBP = 'GBP',
  GHS = 'GHS',
  GMD = 'GMD',
  GTQ = 'GTQ',
  GYD = 'GYD',
  HKD = 'HKD',
  HUF = 'HUF',
  IDR = 'IDR',
  ILS = 'ILS',
  INR = 'INR',
  JMD = 'JMD',
  JOD = 'JOD',
  JPY = 'JPY',
  KES = 'KES',
  KHR = 'KHR',
  KRW = 'KRW',
  KWD = 'KWD',
  KZT = 'KZT',
  LAK = 'LAK',
  LKR = 'LKR',
  MAD = 'MAD',
  MDL = 'MDL',
  MGA = 'MGA',
  MKD = 'MKD',
  MNT = 'MNT',
  MOP = 'MOP',
  MUR = 'MUR',
  MXN = 'MXN',
  MYR = 'MYR',
  MZN = 'MZN',
  NAD = 'NAD',
  NGN = 'NGN',
  NOK = 'NOK',
  NZD = 'NZD',
  OMR = 'OMR',
  PEN = 'PEN',
  PHP = 'PHP',
  PKR = 'PKR',
  PLN = 'PLN',
  PYG = 'PYG',
  QAR = 'QAR',
  RON = 'RON',
  RSD = 'RSD',
  RWF = 'RWF',
  SAR = 'SAR',
  SEK = 'SEK',
  SGD = 'SGD',
  THB = 'THB',
  TND = 'TND',
  TRY = 'TRY',
  TTD = 'TTD',
  TWD = 'TWD',
  TZS = 'TZS',
  USD = 'USD',
  UYU = 'UYU',
  UZS = 'UZS',
  VND = 'VND',
  XAF = 'XAF',
  XCD = 'XCD',
  XOF = 'XOF',
  ZAR = 'ZAR',
}

export const NON_DECIMAL_CURRENCIES = [Currency.KRW, Currency.JPY, Currency.VND, Currency.RWF, Currency.XOF, Currency.XAF];

export enum CurrencySymbol {
  AED = 'AED',
  ALL = 'L',
  AMD = '֏',
  AOA = 'AOA',
  ARS = 'ARS',
  AUD = 'AUD',
  AZN = 'AZN',
  BAM = 'BAM',
  BDT = 'BDT',
  BGN = 'лв',
  BHD = '.د.ب',
  BND = 'BND',
  BOB = 'BOB',
  BRL = 'R$',
  BSD = 'BSD',
  BTN = 'BTN',
  BWP = 'P',
  CAD = 'CAD',
  CHF = 'CHF',
  CLP = 'CLP',
  CNY = 'CN¥',
  COP = 'COP',
  CRC = 'CRC',
  CZK = 'Kč',
  DKK = 'DKK',
  DOP = 'RD$',
  DZD = 'DZD',
  EGP = 'E£',
  ETB = 'ETB',
  EUR = '€',
  GBP = '£',
  GHS = 'GHS',
  GMD = 'GMD',
  GTQ = 'GTQ',
  GYD = 'GYD',
  HKD = 'HKD',
  HUF = 'Ft',
  IDR = 'Rp',
  ILS = '₪',
  INR = '₹',
  JMD = 'J$',
  JOD = 'JOD',
  JPY = '¥',
  KES = 'KSh',
  KHR = 'KHR',
  KRW = '₩',
  KWD = 'د.ك',
  KZT = '₸',
  LAK = 'LAK',
  LKR = 'Rs',
  MAD = 'د.م.',
  MDL = 'MDL',
  MGA = 'MGA',
  MKD = 'MKD',
  MNT = 'MNT',
  MOP = 'MOP',
  MUR = 'MUR',
  MXN = 'M$',
  MYR = 'RM',
  MZN = 'MZN',
  NAD = 'NAD',
  NGN = 'NGN',
  NOK = 'NOK',
  NZD = 'NZD',
  OMR = 'ر.ع.',
  PEN = 'PEN',
  PHP = '₱',
  PKR = '₨',
  PLN = 'zł',
  PYG = 'PYG',
  QAR = 'ر.ق',
  RON = 'RON',
  RSD = 'RSD',
  RWF = 'RWF',
  SAR = '﷼',
  SEK = 'SEK',
  SGD = 'SGD',
  THB = '฿',
  TND = 'TND',
  TRY = '₺',
  TTD = 'TTD',
  TWD = 'NT$',
  TZS = 'TZS',
  USD = '$',
  UYU = '$U',
  UZS = 'UZS',
  VND = '₫',
  XAF = 'XAF',
  XCD = 'XCD',
  XOF = 'XOF',
  ZAR = 'R',
}

export const COUNTRY_CURRENCIES: Record<StripeSupportedCountryCodes, Currency> = {
  [StripeSupportedCountryCodes.AE]: Currency.AED,
  [StripeSupportedCountryCodes.AG]: Currency.XCD,
  [StripeSupportedCountryCodes.AL]: Currency.ALL,
  [StripeSupportedCountryCodes.AM]: Currency.AMD,
  [StripeSupportedCountryCodes.AO]: Currency.AOA,
  [StripeSupportedCountryCodes.AR]: Currency.ARS,
  [StripeSupportedCountryCodes.AT]: Currency.EUR,
  [StripeSupportedCountryCodes.AU]: Currency.AUD,
  [StripeSupportedCountryCodes.AZ]: Currency.AZN,
  [StripeSupportedCountryCodes.BA]: Currency.BAM,
  [StripeSupportedCountryCodes.BD]: Currency.BDT,
  [StripeSupportedCountryCodes.BE]: Currency.EUR,
  [StripeSupportedCountryCodes.BG]: Currency.BGN,
  [StripeSupportedCountryCodes.BH]: Currency.BHD,
  [StripeSupportedCountryCodes.BJ]: Currency.XOF,
  [StripeSupportedCountryCodes.BN]: Currency.BND,
  [StripeSupportedCountryCodes.BO]: Currency.BOB,
  [StripeSupportedCountryCodes.BR]: Currency.BRL,
  [StripeSupportedCountryCodes.BS]: Currency.BSD,
  [StripeSupportedCountryCodes.BT]: Currency.BTN,
  [StripeSupportedCountryCodes.BW]: Currency.BWP,
  [StripeSupportedCountryCodes.CA]: Currency.CAD,
  [StripeSupportedCountryCodes.CH]: Currency.CHF,
  [StripeSupportedCountryCodes.CI]: Currency.XOF,
  [StripeSupportedCountryCodes.CL]: Currency.CLP,
  [StripeSupportedCountryCodes.CO]: Currency.COP,
  [StripeSupportedCountryCodes.CR]: Currency.CRC,
  [StripeSupportedCountryCodes.CY]: Currency.EUR,
  [StripeSupportedCountryCodes.CZ]: Currency.CZK,
  [StripeSupportedCountryCodes.DE]: Currency.EUR,
  [StripeSupportedCountryCodes.DK]: Currency.DKK,
  [StripeSupportedCountryCodes.DO]: Currency.DOP,
  [StripeSupportedCountryCodes.DZ]: Currency.DZD,
  [StripeSupportedCountryCodes.EC]: Currency.USD,
  [StripeSupportedCountryCodes.EE]: Currency.EUR,
  [StripeSupportedCountryCodes.EG]: Currency.EGP,
  [StripeSupportedCountryCodes.ES]: Currency.EUR,
  [StripeSupportedCountryCodes.ET]: Currency.ETB,
  [StripeSupportedCountryCodes.FI]: Currency.EUR,
  [StripeSupportedCountryCodes.FR]: Currency.EUR,
  [StripeSupportedCountryCodes.GA]: Currency.XAF,
  [StripeSupportedCountryCodes.GB]: Currency.GBP,
  [StripeSupportedCountryCodes.GH]: Currency.GHS,
  [StripeSupportedCountryCodes.GI]: Currency.GBP,
  [StripeSupportedCountryCodes.GM]: Currency.GMD,
  [StripeSupportedCountryCodes.GR]: Currency.EUR,
  [StripeSupportedCountryCodes.GT]: Currency.GTQ,
  [StripeSupportedCountryCodes.GY]: Currency.GYD,
  [StripeSupportedCountryCodes.HK]: Currency.HKD,
  [StripeSupportedCountryCodes.HR]: Currency.EUR,
  [StripeSupportedCountryCodes.HU]: Currency.HUF,
  [StripeSupportedCountryCodes.ID]: Currency.IDR,
  [StripeSupportedCountryCodes.IE]: Currency.EUR,
  [StripeSupportedCountryCodes.IL]: Currency.ILS,
  [StripeSupportedCountryCodes.IN]: Currency.INR,
  [StripeSupportedCountryCodes.IS]: Currency.EUR,
  [StripeSupportedCountryCodes.IT]: Currency.EUR,
  [StripeSupportedCountryCodes.JM]: Currency.JMD,
  [StripeSupportedCountryCodes.JO]: Currency.JOD,
  [StripeSupportedCountryCodes.JP]: Currency.JPY,
  [StripeSupportedCountryCodes.KE]: Currency.KES,
  [StripeSupportedCountryCodes.KH]: Currency.KHR,
  [StripeSupportedCountryCodes.KR]: Currency.KRW,
  [StripeSupportedCountryCodes.KW]: Currency.KWD,
  [StripeSupportedCountryCodes.KZ]: Currency.KZT,
  [StripeSupportedCountryCodes.LA]: Currency.LAK,
  [StripeSupportedCountryCodes.LC]: Currency.XCD,
  [StripeSupportedCountryCodes.LI]: Currency.CHF,
  [StripeSupportedCountryCodes.LK]: Currency.LKR,
  [StripeSupportedCountryCodes.LT]: Currency.EUR,
  [StripeSupportedCountryCodes.LU]: Currency.EUR,
  [StripeSupportedCountryCodes.LV]: Currency.EUR,
  [StripeSupportedCountryCodes.MA]: Currency.MAD,
  [StripeSupportedCountryCodes.MC]: Currency.EUR,
  [StripeSupportedCountryCodes.MD]: Currency.MDL,
  [StripeSupportedCountryCodes.MG]: Currency.MGA,
  [StripeSupportedCountryCodes.MK]: Currency.MKD,
  [StripeSupportedCountryCodes.MN]: Currency.MNT,
  [StripeSupportedCountryCodes.MO]: Currency.MOP,
  [StripeSupportedCountryCodes.MT]: Currency.EUR,
  [StripeSupportedCountryCodes.MU]: Currency.MUR,
  [StripeSupportedCountryCodes.MX]: Currency.MXN,
  [StripeSupportedCountryCodes.MY]: Currency.MYR,
  [StripeSupportedCountryCodes.MZ]: Currency.MZN,
  [StripeSupportedCountryCodes.NA]: Currency.NAD,
  [StripeSupportedCountryCodes.NE]: Currency.XOF,
  [StripeSupportedCountryCodes.NG]: Currency.NGN,
  [StripeSupportedCountryCodes.NL]: Currency.EUR,
  [StripeSupportedCountryCodes.NO]: Currency.NOK,
  [StripeSupportedCountryCodes.NZ]: Currency.NZD,
  [StripeSupportedCountryCodes.OM]: Currency.OMR,
  [StripeSupportedCountryCodes.PA]: Currency.USD,
  [StripeSupportedCountryCodes.PE]: Currency.PEN,
  [StripeSupportedCountryCodes.PH]: Currency.PHP,
  [StripeSupportedCountryCodes.PK]: Currency.PKR,
  [StripeSupportedCountryCodes.PL]: Currency.PLN,
  [StripeSupportedCountryCodes.PT]: Currency.EUR,
  [StripeSupportedCountryCodes.PY]: Currency.PYG,
  [StripeSupportedCountryCodes.QA]: Currency.QAR,
  [StripeSupportedCountryCodes.RO]: Currency.RON,
  [StripeSupportedCountryCodes.RS]: Currency.RSD,
  [StripeSupportedCountryCodes.RW]: Currency.RWF,
  [StripeSupportedCountryCodes.SA]: Currency.SAR,
  [StripeSupportedCountryCodes.SE]: Currency.SEK,
  [StripeSupportedCountryCodes.SG]: Currency.SGD,
  [StripeSupportedCountryCodes.SI]: Currency.EUR,
  [StripeSupportedCountryCodes.SK]: Currency.EUR,
  [StripeSupportedCountryCodes.SM]: Currency.EUR,
  [StripeSupportedCountryCodes.SN]: Currency.XOF,
  [StripeSupportedCountryCodes.SV]: Currency.USD,
  [StripeSupportedCountryCodes.TH]: Currency.THB,
  [StripeSupportedCountryCodes.TN]: Currency.TND,
  [StripeSupportedCountryCodes.TR]: Currency.TRY,
  [StripeSupportedCountryCodes.TT]: Currency.TTD,
  [StripeSupportedCountryCodes.TW]: Currency.TWD,
  [StripeSupportedCountryCodes.TZ]: Currency.TZS,
  [StripeSupportedCountryCodes.US]: Currency.USD,
  [StripeSupportedCountryCodes.UY]: Currency.UYU,
  [StripeSupportedCountryCodes.UZ]: Currency.UZS,
  [StripeSupportedCountryCodes.VN]: Currency.VND,
  [StripeSupportedCountryCodes.ZA]: Currency.ZAR,
};

export const getSupportedCountryCode = ({
  agreementType,
  code,
}: {
  agreementType: StripeAccountServiceAgreement;
  code: string;
}): StripeSupportedCountryCodes | SupportedCountryCodeForFullAgreement => {
  if (
    agreementType === StripeAccountServiceAgreement.RECIPIENT &&
    Object.values(StripeSupportedCountryCodes).includes(code as StripeSupportedCountryCodes)
  ) {
    return code as StripeSupportedCountryCodes;
  }
  if (Object.values(SupportedCountryCodeForFullAgreement).includes(code as SupportedCountryCodeForFullAgreement)) {
    return code as SupportedCountryCodeForFullAgreement;
  }
  throw new Error('unsupported country code');
};

// this is meant to check if the country code is supported at all,
// not to validate if the country code is supported by the service agreement for the brand
export const isStripeSupportedCountryCodes = (code: string): code is StripeSupportedCountryCodes => {
  return Object.values(StripeSupportedCountryCodes).includes(code as StripeSupportedCountryCodes);
};

export const getCurrencyCode = ({ code }: { code?: string }): Currency => {
  if (code && Object.values(Currency).includes(code.toUpperCase() as Currency)) {
    return code.toUpperCase() as Currency;
  }

  throw new Error('unsupported currency code');
};

export const getCurrencyForCountry = ({ country }: { country: string }): Currency => {
  // using recipient agreement to enable getting currency for any Stripe supported countries
  const countryCode = getSupportedCountryCode({ agreementType: StripeAccountServiceAgreement.RECIPIENT, code: country });
  return COUNTRY_CURRENCIES[countryCode];
};

// these are the countries which are not supported for host commissions with full agreement accounts
export const HOST_COMMISSIONS_RESTRICTED_COUNTRIES_FOR_FULL_AGREEMENT = [
  SupportedCountryCodeForFullAgreement.AE,
  SupportedCountryCodeForFullAgreement.AU,
  SupportedCountryCodeForFullAgreement.CA,
  SupportedCountryCodeForFullAgreement.JP,
  SupportedCountryCodeForFullAgreement.MX,
];

export const UpdateBrandExperienceSortingRequestSchema = z.object({
  enableAutomaticExperienceSorting: z.boolean(),
});

export const UpdateBrandExperienceSorting = makeEndpoint({
  method: 'PATCH',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/experiences-sorting`,
  responseSchema: z.void(),
  requestSchema: UpdateBrandExperienceSortingRequestSchema,
});

export const PublicBrandDetailResponseSchema = BrandSchema.pick({
  id: true,
  name: true,
  countryCode: true,
  currency: true,
  location: true,
  organizationId: true,
}).extend({
  externalReferenceId: z.string().nullable(),
  location: LocationSchema.omit({
    createdAt: true,
    updatedAt: true,
  }).nullable(),
  logoUrl: z.string().nullable(),
  logo: IMediaReferenceSchema.nullable(),
});

export type PublicBrandDetailResponse = z.output<typeof PublicBrandDetailResponseSchema>;

export const GetPublicBrandDetails = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}`,
  responseSchema: PublicBrandDetailResponseSchema,
  requestSchema: z.void(),
});

const GetBrandCoreTranslationsResponseSchema = z.object({
  vibes: z.array(TranslationWithIdSchema.omit({ createdAt: true, updatedAt: true })),
  categories: z.array(TranslationWithIdSchema.omit({ createdAt: true, updatedAt: true })),
  cancellationPolicies: z.array(TranslationWithIdSchema.omit({ createdAt: true, updatedAt: true })),
  reschedulePolicies: z.array(TranslationWithIdSchema.omit({ createdAt: true, updatedAt: true })),
  taxes: z.array(TranslationWithIdSchema.omit({ createdAt: true, updatedAt: true })),
});

const GetBrandCoreTranslationsQuerySchema = z
  .object({
    language: z.nativeEnum(LanguageCode).optional(),
  })
  .default({});

export const GetBrandCoreTranslationsEndpoint = makeEndpoint({
  method: 'GET',
  url: (params: { [key: string]: unknown; query: { language?: LanguageCode } }) =>
    params.query?.language
      ? `v1/brands/${params['brandId'] as string}/i18n?${buildQueryString({ language: params.query.language })}`
      : `v1/brands/${params['brandId'] as string}/i18n`,
  responseSchema: GetBrandCoreTranslationsResponseSchema,
  requestSchema: z.void(),
  querySchema: GetBrandCoreTranslationsQuerySchema,
});

const PatchBrandCoreTranslationsRequestSchema = z.object({
  vibes: z.array(TranslationSchema.omit({ createdAt: true, updatedAt: true })),
  categories: z.array(TranslationSchema.omit({ createdAt: true, updatedAt: true })),
  cancellationPolicies: z.array(TranslationSchema.omit({ createdAt: true, updatedAt: true })),
  reschedulePolicies: z.array(TranslationSchema.omit({ createdAt: true, updatedAt: true })),
  taxes: z.array(TranslationSchema.omit({ createdAt: true, updatedAt: true })),
});

export const PatchBrandCoreTranslationsEndpoint = makeEndpoint({
  method: 'PATCH',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/i18n`,
  responseSchema: z.void(),
  requestSchema: PatchBrandCoreTranslationsRequestSchema,
});

export const GetBrandParentOrganizationsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/parent-organizations`,
  responseSchema: z
    .object({
      items: z.array(
        z.object({
          id: z.string().uuid(),
          name: z.string(),
        }),
      ),
    })
    .strict(),
  requestSchema: z.void(),
});

export const BrandToReviewResponseSchema = BrandSchema.pick({
  id: true,
  name: true,
  tier: true,
  countryCode: true,
  currency: true,
  creditCardFeePercentage: true,
  isLimited: true,
  propertyType: true,
  defaultLanguageCode: true,
  vision: true,
  pillars: true,
  travelProfile: true,
  availableSpacesAndAmenities: true,
}).extend({
  marketing: BrandMarketingSchema,
  ownerContact: z
    .object({
      firstName: z.string(),
      lastName: z.string(),
      email: z.string(),
    })
    .nullable(),
  invitedBy: z.string().nullable(),
  applicationSubmittedDate: z.string().datetime().nullable(),
});

export const GetBrandsToReviewEndpoint = makeEndpoint({
  method: 'GET',
  url: () => 'v1/brands/review',
  responseSchema: paginateResponseSchema(BrandToReviewResponseSchema),
  requestSchema: z.void(),
});

export const GetBrandToReviewEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/review`,
  responseSchema: BrandToReviewResponseSchema,
  requestSchema: z.void(),
});

export const PostBrandReviewSubmissionRequestSchema = z.object({
  status: z.nativeEnum(BrandReviewStatus),
});

export const PostBrandReviewSubmissionEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/review`,
  responseSchema: z.void(),
  requestSchema: PostBrandReviewSubmissionRequestSchema,
});

export const GetBrandMarketingDetailsEndpoint = makeEndpoint({
  method: 'GET',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/review`,
  responseSchema: z.object({ marketing: BrandMarketingSchema.unwrap().nullable() }),
  requestSchema: z.void(),
});

export const JoinMatchMakingWaitlistEndpoint = makeEndpoint({
  method: 'POST',
  url: ({ brandId }: { brandId: string }) => `v1/brands/${brandId}/joinWaitingList`,
  responseSchema: z.void(),
  requestSchema: z.void(),
});
